import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';
import useFacts from './useFacts';

const DogWalkingFaqs: React.FC<ServiceFaq> = ({
  localizedLinkUrls: links,
  facts,
  locationName,
  localityType,
  htmlRef,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersCanProvideSpecialCarePct,
    sittersWithMinResponseTimeIn60minPct,
    yearsOfExperienceNum,
  } = useFacts(facts);

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="dog-walking-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Dog Walking FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const { yearMonthLastUpdated, asCurrency: formattedAmount } =
              medianListPricePerUnitLocalCurrencyNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a dog walker in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a dog walker in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} dog walker?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Dog walkers on Rover are free to set their rates. The median cost to hire a
                        dog walker in {cityName} on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per walk, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A dog walker’s rate may also change as you customize your booking to fit
                        your and your dog’s needs.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Dog walkers on Rover are free to set their rates. The median cost to hire a
                        dog walker in {neighborhoodName} on Rover as of {yearMonthLastUpdated} was
                        about {formattedAmount} per walk, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A dog walker’s rate may also change as you customize your booking to fit
                        your and your dog’s needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Dog walkers on Rover are free to set their rates. The median cost to hire a
                        {locationName} dog walker on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per walk, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A dog walker’s rate may also change as you customize your booking to fit
                        your and your dog’s needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 2 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion question={t`How do I contact a ${locationName} dog walker?`}>
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} dog walker for the first time, visit
                    the dog walker’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a dog walker before,
                    learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 3 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: sittersCount } = maxSearchableSittersNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many dog walkers are in ${cityName}?`,
                  neighborhood: `How many dog walkers are in ${neighborhoodName}?`,
                  other: `How many ${locationName} dog walkers are there?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {sittersCount} dog walkers in{' '}
                        {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect dog walker near you. As a reminder, dog
                        walkers joining Rover must pass a background check for you and your dog’s
                        safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {sittersCount} dog walkers in{' '}
                        {neighborhoodName}. You can filter, sort, expand your radius, read reviews,
                        and compare pricing to find the perfect dog walker near you. As a reminder,
                        dog walkers joining Rover must pass a background check for you and your
                        dog’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {sittersCount} {locationName} dog
                        walkers. You can filter, sort, expand your radius, read reviews, and compare
                        pricing to find the perfect dog walker near you. As a reminder, dog walkers
                        joining Rover must pass a background check for you and your dog’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a dog walker in ${cityName}?`,
            neighborhood: `How long will it take for me to find a dog walker in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} dog walker?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple dog walkers about your booking.
                    Typically, {percent} of {locationName} dog walkers respond in under an hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Rover makes it easy to reach out to multiple dog walkers about your booking.
                Typically, dog walkers on Rover respond in under an hour.
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are dog walkers in ${cityName}?`,
            neighborhood: `How experienced are dog walkers in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} dog walkers?`,
          })}
        >
          <>
            {/* we have num years of experience and avg repeat customers */}
            {yearsOfExperienceNum &&
              avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const yearsOfExperience = yearsOfExperienceNum.raw;
                const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Dog walkers in {cityName} have an average of {yearsOfExperience} years of
                          experience. Dog walkers with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from dog walker
                          to dog walker, but you can view reviews, years of experience, and number
                          of repeat clients when you compare dog walkers in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Dog walkers in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                          years of experience. Dog walkers with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from dog walker
                          to dog walker, but you can view reviews, years of experience, and number
                          of repeat clients when you compare dog walkers in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          {locationName} dog walkers have an average of {yearsOfExperience} years of
                          experience. Dog walkers with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from dog walker
                          to dog walker, but you can view reviews, years of experience, and number
                          of repeat clients when you compare dog walkers.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we have avg repeat customers, but not num years of experience */}
            {!yearsOfExperienceNum &&
              avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Dog walkers with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from dog walker
                          to dog walker, but you can view reviews, years of experience, and number
                          of repeat clients when you compare dog walkers in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Dog walkers with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from dog walker
                          to dog walker, but you can view reviews, years of experience, and number
                          of repeat clients when you compare dog walkers in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          Dog walkers with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from dog walker
                          to dog walker, but you can view reviews, years of experience, and number
                          of repeat clients when you compare dog walkers.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we have num years of experience, but not avg repeat customers */}
            {yearsOfExperienceNum &&
              !avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const yearsOfExperience = yearsOfExperienceNum.raw;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Dog walkers in {cityName} have an average of {yearsOfExperience} years of
                          experience. This can vary widely from dog walker to dog walker, but you
                          can view reviews, years of experience, and number of repeat clients when
                          comparing dog walkers available in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Dog walkers in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                          years of experience. This can vary widely from dog walker to dog walker,
                          but you can view reviews, years of experience, and number of repeat
                          clients when comparing dog walkers available in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          {locationName} dog walkers have an average of {yearsOfExperience} years of
                          experience. This can vary widely from dog walker to dog walker, but you
                          can view reviews, years of experience, and number of repeat clients when
                          comparing dog walkers.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we don't have num years of experience or avg repeat customers */}
            {!yearsOfExperienceNum && !avgRepeatCustomerAmongSittersWithRepeatNum && (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from dog walker to dog walker, but you can view
                      reviews, years of experience, and number of repeat clients when comparing dog
                      walkers available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from dog walker to dog walker, but you can view
                      reviews, years of experience, and number of repeat clients when comparing dog
                      walkers available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from dog walker to dog walker, but you can view
                      reviews, years of experience, and number of repeat clients when comparing dog
                      walkers.
                    </Trans>
                  }
                />
              </Paragraph>
            )}
          </>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of services do dog walkers in ${cityName} offer on Rover?`,
            neighborhood: `What sort of services do dog walkers in ${neighborhoodName} offer on Rover?`,
            other: `What sort of services do ${locationName} dog walkers offer on Rover?`,
          })}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                You can’t always predict a super busy day at work, but you can anticipate your dog’s
                needs. Instead of rushing home at lunch, book a dog walker to give your dog a 30- or
                60-minute dog walk. Your dog walker can stop by as many times as you need—on
                whatever days you need them. With the Rover app, get a comprehensive Rover Card from
                your dog walker that includes:
              </Trans>
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans comment="Part of a list of things included on Rover Cards">
                  Start and stop times
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list of things included on Rover Cards">
                  A map of their walk with total distance
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list of things included on Rover Cards">
                  Pee, poo, food, and water breaks
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list of things included on Rover Cards">
                  Cute photos and a personalized note
                </Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        {maxSearchableSittersNum && (
          <FrequentlyAskedQuestion
            question={t`My dog has special needs. Can I find dog walking on Rover that can accommodate my dog’s needs?`}
          >
            {sittersCanProvideSpecialCarePct
              ? (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  const specialCarePercent = sittersCanProvideSpecialCarePct.asPercent;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} dog walkers in {cityName} on Rover, so you’re
                            sure to find the perfect match for your dog! In {cityName},{' '}
                            {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect dog walker near you. As a
                            reminder, all dog walkers on Rover have been background checked for you
                            and your dog’s safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} dog walkers in {neighborhoodName} on Rover, so
                            you’re sure to find the perfect match for your dog! In{' '}
                            {neighborhoodName}, {specialCarePercent} have experience with senior
                            dogs and dogs with special needs. We know each dog is unique and may
                            have specific care instructions. You can filter, sort, expand your
                            radius, read reviews, and compare pricing to find the perfect dog walker
                            near you. As a reminder, all dog walkers on Rover have been background
                            checked for you and your dog’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} dog walkers on Rover, so you’re
                            sure to find the perfect match for your dog! In {neighborhoodName},{' '}
                            {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect dog walker near you. As a
                            reminder, all dog walkers on Rover have been background checked for you
                            and your dog’s safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()
              : (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} dog walkers in {cityName} on Rover, so you’re
                            sure to find the perfect match for your dog! We understand that each dog
                            is unique and may require specific instructions. You can filter, sort,
                            expand your radius, read reviews, and compare pricing to find the
                            perfect dog walker near you. As a reminder, dog walkers joining Rover
                            must pass a background check for you and your dog’s safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} dog walkers in {neighborhoodName} on Rover, so
                            you’re sure to find the perfect match for your dog! We understand that
                            each dog is unique and may require specific instructions. You can
                            filter, sort, expand your radius, read reviews, and compare pricing to
                            find the perfect dog walker near you. As a reminder, dog walkers joining
                            Rover must pass a background check for you and your dog’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} dog walkers on Rover, so you’re
                            sure to find the perfect match for your dog! We understand that each dog
                            is unique and may require specific instructions. You can filter, sort,
                            expand your radius, read reviews, and compare pricing to find the
                            perfect dog walker near you. As a reminder, dog walkers joining Rover
                            must pass a background check for you and your dog’s safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()}
          </FrequentlyAskedQuestion>
        )}

        {/* 8 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do dog walkers in ${cityName} pass a background check?`,
            neighborhood: `Do dog walkers in ${neighborhoodName} pass a background check?`,
            other: `Do ${locationName} dog walkers pass a background check?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! Dog walkers joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your dog walker through messages on
              Rover for delightful photo updates. The Rover team is available 24/7, and your dog
              walker has access to advice from qualified veterinary professionals. In the rare event
              of any issues during a booking, rest easy knowing your dog is covered by the Rover
              Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 9 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I cancel a dog walking service and receive a refund?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your dog walker requested to cancel this booking,
                      then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your dog walking
                    service. You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your dog walker’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DogWalkingFaqs;
